import { useDisclosure } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  EventRevisionState,
  SourcingEventStatus,
  type SourcingEventV2Dto,
  useCreateEventChangeMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { urls } from "../../../../urls";
import { changesEditWholeEvent, openChangeDraft } from "./changeScope";
import { changeWindowOf } from "./changeWindow";

export const useChangeFlow = (event: SourcingEventV2Dto) => {
  const { t } = useTranslation();
  const apiError = useApiError();
  const editor = useDisclosure();
  const navigate = useNavigate();
  const [create, { isLoading: isStarting }] = useCreateEventChangeMutation();

  const isPrivate = event.eventContext.type === "Private";
  const changes = event.changes ?? [];
  const draft = openChangeDraft(event);
  const pending = [...changes].reverse().find((c) => c.state === EventRevisionState.PublicationPending);
  const published = changes.filter((c) => c.state === EventRevisionState.Published);
  const canStartChange =
    event.status === SourcingEventStatus.Published && !draft && !pending && changeWindowOf(event) !== "closed";

  const openChange = () => {
    if (changesEditWholeEvent(event)) navigate(urls.publicEvents.edit.go(event.id).info);
    else editor.onOpen();
  };

  const startChange = async () => {
    try {
      await create({ eventId: event.id }).unwrap();
      openChange();
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const actionLabel = draft
    ? t("Continue the change")
    : isPrivate
      ? t("Change the request")
      : t("Change the announcement");

  return { isPrivate, draft, pending, published, canStartChange, isStarting, editor, openChange, startChange, actionLabel };
};
